import {
  ref,
  computed,
  watch,
  inject,
  onUnmounted,
} from '@vue/composition-api'

// 自動延長の発火予定（= expireAt - 通知分）までの残り時間を算出する。
// 発火は終了予定の1分前（バックエンド Table::AUTO_EXTENSION_NOTICE_MINUTES と一致。終了通知設定とは独立）。
export const AUTO_EXTENSION_NOTICE_MINUTES = 1

// 予告カウントダウンを表示し始める発火予定までのリード時間（分）。
// これより前は表示しない（ON/OFFはトグルで常時操作可能なので停止手段は失われない）。
export const NOTICE_LEAD_MINUTES = 10

// 発火予定を過ぎてから「自動延長中…」を出し続ける猶予（秒）。
// 通常は数秒で延長が反映されるが、sidekiq遅延も見込む。これを超えたら（＝異常）表示を畳む。
export const AUTO_EXTENDING_GRACE_SECONDS = 180

const NOTICE_LEAD_SECONDS = NOTICE_LEAD_MINUTES * 60

const fireAtMs = (expireAt, noticeMinutes) => {
  const minutes = Number(noticeMinutes) > 0 ? Number(noticeMinutes) : AUTO_EXTENSION_NOTICE_MINUTES

  return Date.parse(expireAt) - minutes * 60000
}

// 発火予定までの残り分数（切り上げ）
export const minutesUntilAutoExtension = (expireAt, noticeMinutes, now = new Date()) => {
  if (!expireAt) return null

  return Math.ceil((fireAtMs(expireAt, noticeMinutes) - now.getTime()) / 60000)
}

// 発火予定までの残り秒数（切り上げ）。1分未満の予告を秒で出すのに使う。
export const secondsUntilAutoExtension = (expireAt, noticeMinutes, now = new Date()) => {
  if (!expireAt) return null

  return Math.ceil((fireAtMs(expireAt, noticeMinutes) - now.getTime()) / 1000)
}

// 終了予定(expireAt)の変化が自動延長の発火によるものか。
// サーバーが配信する autoExtendedAt（最後に自動延長した時刻）が新しい値になったときだけ true。
// 時刻から推定すると「終了予定を過ぎたまま接客中の卓を手動延長した」ケースを
// 自動延長と誤判定するため、サーバーの事実だけを根拠にする。
export const isAutoExtensionFire = (previousAutoExtendedAt, nextAutoExtendedAt) => {
  if (!nextAutoExtendedAt) return false

  return previousAutoExtendedAt !== nextAutoExtendedAt
}

// 自動延長の進行状態（予告中か・処理中か）を一箇所に集約する。
// 予告カウントダウン(AutoExtensionCountdown)と手動延長の一時無効化(ExtensionColumn)が
// 同じ判定を共有するためのcomposable。引数はいずれも値を返す関数（getter）で渡す。
export const useAutoExtensionStatus = ({
  enabled,
  expireAt,
  courseName,
  noticeMinutes = () => AUTO_EXTENSION_NOTICE_MINUTES,
}) => {
  const now = ref(new Date())
  let intervalId = null
  const startTicking = () => {
    if (intervalId) return
    intervalId = setInterval(() => { now.value = new Date() }, 1000)
  }
  const stopTicking = () => {
    if (!intervalId) return
    clearInterval(intervalId)
    intervalId = null
  }
  onUnmounted(stopTicking)

  // 追跡するのは「自動延長ON」「終了予定あり」「延長コース解決済み」の3つが揃った卓だけ。
  // コース未解決の卓はサーバー側(PersistenceReport#auto_extension_enabled_table?)が
  // ジョブ自体を予約しないため、予告を出すと必ず嘘になる。
  const shouldTrack = computed(() => Boolean(enabled() && expireAt() && courseName()))
  watch(shouldTrack, tracking => (tracking ? startTicking() : stopTicking()), { immediate: true })

  // ドロワーが自動延長発火→明細再取得中かどうか（ドロワー未配下ならfalse）
  const isAutoExtending = inject('isAutoExtending', ref(false))

  const secondsLeft = computed(() => secondsUntilAutoExtension(expireAt(), noticeMinutes(), now.value))

  // 「処理中」の条件:
  // - isAutoExtending: 発火を検知して明細(getTable)を再取得中
  // - 発火予定を過ぎた直後の待ち時間（発火→延長がサーバで作られ配信が届くまで。猶予内に限定）
  const isProcessing = computed(() => shouldTrack.value && (
    isAutoExtending.value
    || (
      secondsLeft.value !== null
      && secondsLeft.value <= 0
      && secondsLeft.value > -AUTO_EXTENDING_GRACE_SECONDS
    )
  ))

  // 発火予定がリード時間内に迫っている（＝予告を出す）状態。
  // リードより前(例: あと3時間)は出さず、発火間際だけ予告する。
  const isNoticing = computed(() => (
    shouldTrack.value
    && secondsLeft.value !== null
    && secondsLeft.value > 0
    && secondsLeft.value <= NOTICE_LEAD_SECONDS
  ))

  const visible = computed(() => isProcessing.value || isNoticing.value)

  // 処理中は「自動延長中…」、それ以外は1分以上は分（切り上げ）・1分未満は秒で表示する
  const label = computed(() => {
    if (isProcessing.value) return '自動延長中…'

    const s = secondsLeft.value
    if (s === null) return ''
    if (s >= 60) return `あと${Math.ceil(s / 60)}分で自動延長します`

    return `あと${s}秒で自動延長します`
  })

  return {
    now,
    shouldTrack,
    secondsLeft,
    isProcessing,
    isNoticing,
    visible,
    label,
  }
}

export default useAutoExtensionStatus
